import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { router, Link } from '@inertiajs/react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { LogoIcon } from '@/components/landing/Logo'

const schema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
})

type FormValues = z.infer<typeof schema>

interface Props {
  errors: { base?: string[] }
}

export default function SignIn({ errors }: Props) {
  const {
    register,
    handleSubmit,
    formState: { errors: fieldErrors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) })

  function onSubmit(data: FormValues) {
    router.post('/sign_in', { user: data }, { preserveScroll: true })
  }

  return (
    <div className="relative isolate flex min-h-screen items-center justify-center overflow-hidden p-4">
      {/* Ambient brand wash — soft, accent-only */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 engineer-grid opacity-30"
        style={{
          maskImage:
            'radial-gradient(ellipse at center, black 0%, transparent 70%)',
          WebkitMaskImage:
            'radial-gradient(ellipse at center, black 0%, transparent 70%)',
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 left-1/2 -z-10 h-[480px] w-[680px] -translate-x-1/2 rounded-full"
        style={{
          background:
            'radial-gradient(ellipse at center, hsl(var(--brand-2) / 0.18) 0%, transparent 60%)',
        }}
      />

      <div className="surface-card w-full max-w-sm p-8">
        <Link href="/" className="mb-6 inline-flex items-center gap-2 font-heading text-base font-semibold tracking-tight">
          <LogoIcon />
          KonexZero
        </Link>

        <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
          Welcome back
        </div>
        <h1 className="mt-1 font-heading text-2xl font-bold tracking-tight">
          Sign in
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Enter your email and password to access your dashboard.
        </p>

        {errors.base?.[0] && (
          <Alert variant="destructive" className="mt-6">
            <AlertDescription>{errors.base[0]}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="email" className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              className="h-11 rounded-md border-foreground/10 bg-background/50"
              {...register('email')}
            />
            {fieldErrors.email && (
              <p className="text-xs text-destructive">{fieldErrors.email.message}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="password" className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
              Password
            </Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              className="h-11 rounded-md border-foreground/10 bg-background/50"
              {...register('password')}
            />
            {fieldErrors.password && (
              <p className="text-xs text-destructive">{fieldErrors.password.message}</p>
            )}
          </div>

          <Button
            type="submit"
            disabled={isSubmitting}
            className="h-11 w-full rounded-md text-sm font-semibold"
          >
            {isSubmitting ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          Don&apos;t have an account?{' '}
          <Link href="/sign_up" className="font-medium text-foreground underline underline-offset-4 hover:text-primary">
            Sign up
          </Link>
        </p>
      </div>
    </div>
  )
}
